/**
 * LeaderboardTable - Reusable table component for displaying leaderboard entries
 * Used by both Rankings (private) and PublicLeaderboard (public) pages
 */
import { Link } from 'react-router-dom'
import { Clock } from 'lucide-react'
import rankingsService from '../../services/rankingsService'

/**
 * Level Badge Component
 */
export const LevelBadge = ({ level }) => {
  const getLevelColor = (lvl) => {
    if (lvl <= 3) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
    if (lvl <= 6) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    if (lvl <= 9) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
    return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(level)}`}
    >
      Lv {level}
    </span>
  )
}

/**
 * Single row component for the leaderboard table
 */
const LeaderboardRow = ({ entry, rank, currentUserId, highlightedPublicName, showTimeColumn }) => {
  const isCurrentUser = entry.userId === currentUserId
  const isHighlighted = highlightedPublicName && entry.user?.publicName === highlightedPublicName
  const rankInfo = rankingsService.formatRank(rank)

  return (
    <tr
      className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
        isCurrentUser || isHighlighted
          ? 'bg-yellow-50 dark:bg-yellow-900/20 ring-2 ring-yellow-400 dark:ring-yellow-500'
          : ''
      }`}
    >
      <td className="py-4 px-6">
        <div className="flex items-center">
          {rankInfo.isMedal ? (
            <span className="text-2xl">{rankInfo.display}</span>
          ) : (
            <span className={`font-semibold ${rankInfo.color} dark:text-gray-400`}>
              {rankInfo.display}
            </span>
          )}
        </div>
      </td>
      <td className="py-4 px-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
            {(entry.user?.publicName || 'A')[0].toUpperCase()}
          </div>
          <div>
            {entry.user?.publicName ? (
              <Link
                to={`/u/${entry.user.publicName}`}
                className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {entry.user.publicName}
              </Link>
            ) : (
              <span className="font-medium text-gray-900 dark:text-gray-100">Anonymous</span>
            )}
            {isCurrentUser && (
              <span className="ml-2 text-xs text-primary-600 dark:text-primary-400 font-medium">
                (You)
              </span>
            )}
            {isHighlighted && !isCurrentUser && (
              <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                Featured
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="py-4 px-6">
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          {rankingsService.formatScore(entry)}
        </span>
      </td>
      <td className="py-4 px-6">
        <LevelBadge level={entry.userLevel || entry.level || 1} />
      </td>
      {showTimeColumn && (
        <td className="py-4 px-6">
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4" />
            <span>{rankingsService.formatTime(entry.totalTimeSpent || entry.time)}</span>
          </div>
        </td>
      )}
    </tr>
  )
}

/**
 * Separator row to show "..." between top 10 and current user
 */
const SeparatorRow = ({ colSpan }) => (
  <tr className="bg-gray-50 dark:bg-gray-800/30">
    <td colSpan={colSpan} className="py-2 text-center">
      <span className="text-gray-400 dark:text-gray-500 text-lg">&#8942;</span>
    </td>
  </tr>
)

/**
 * LeaderboardTable - Displays ranked users in a table format
 *
 * Supports "top N + current user" display mode:
 * - If userPosition is provided and user's rank > topCount, shows:
 *   1. Top N entries
 *   2. Separator ("...")
 *   3. Current user's entry with their actual rank
 */
const LeaderboardTable = ({
  entries,
  currentUserId,
  startRank = 1,
  highlightedPublicName,
  showTimeColumn = true,
  userPosition = null, // { rank, entry } - current user's position if outside displayed entries
  topCount = 10 // Number of top entries to show before separator
}) => {
  const colSpan = showTimeColumn ? 5 : 4

  // Determine if we need to show the separator + user row
  const showUserSeparately =
    userPosition &&
    userPosition.rank > topCount &&
    userPosition.entry &&
    !entries.some((e) => e.userId === currentUserId)

  // Limit entries to topCount if we're showing user separately
  const displayEntries = showUserSeparately ? entries.slice(0, topCount) : entries

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <th className="text-left py-3 px-6 text-sm font-medium text-gray-600 dark:text-gray-400 w-20">
              Rank
            </th>
            <th className="text-left py-3 px-6 text-sm font-medium text-gray-600 dark:text-gray-400">
              User
            </th>
            <th className="text-left py-3 px-6 text-sm font-medium text-gray-600 dark:text-gray-400 w-24">
              Score
            </th>
            <th className="text-left py-3 px-6 text-sm font-medium text-gray-600 dark:text-gray-400 w-24">
              Level
            </th>
            {showTimeColumn && (
              <th className="text-left py-3 px-6 text-sm font-medium text-gray-600 dark:text-gray-400 w-24">
                Time
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {displayEntries.map((entry, index) => (
            <LeaderboardRow
              key={entry.id || entry.challengeRankingId || index}
              entry={entry}
              rank={startRank + index}
              currentUserId={currentUserId}
              highlightedPublicName={highlightedPublicName}
              showTimeColumn={showTimeColumn}
            />
          ))}

          {/* Separator and current user's position if outside top N */}
          {showUserSeparately && (
            <>
              <SeparatorRow colSpan={colSpan} />
              <LeaderboardRow
                entry={userPosition.entry}
                rank={userPosition.rank}
                currentUserId={currentUserId}
                highlightedPublicName={highlightedPublicName}
                showTimeColumn={showTimeColumn}
              />
            </>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default LeaderboardTable
